import {
  DialogActions,
  DialogContent,
  Button,
  makeStyles,
  Typography,
  DialogTitle,
  Divider,
  Grid,
} from "@material-ui/core";
import { DatePicker } from "@ofqual/portal-components/components/DatePicker";
import { TextField } from "@ofqual/portal-components/components/TextField";
import React from "react";
import {
  AdditionalEvidenceRequest,
  AdditionalEvidenceRequestStatus,
  StatementsOfAssuranceRequest,
} from "../../../../../domain/AdditionalEvidenceRequest";
import { AttachmentList } from "../../../AttachmentList";

type EvidenceSummaryProps = {
  request: AdditionalEvidenceRequest;
  onClick: (entity: StatementsOfAssuranceRequest | "other") => void;
  onClose: () => void;
  onSubmit: () => void;
  onDownload: (blobId: string) => Promise<Blob>;
};

const useStyles = makeStyles((theme) => ({
  dialogActions: {
    padding: 15,
  },
  body: {
    marginTop: 5,
    marginBottom: 5,
  },
  action: {
    marginLeft: 5,
  },
}));

export const EvidenceSummary: React.FC<EvidenceSummaryProps> = ({
  request,
  onClick,
  onClose,
  onSubmit,
  onDownload,
}) => {
  const classes = useStyles();

  const soaComplete = !request.statementOfAssuranceRequests.some(
    (r) =>
      r.declarationOptionID === null && r.selectedDeclarationOption === null
  );

  const otherEvidenceComplete = request.isOtherEvidenceRequired
    ? request.otherEvidenceResponseText != null
    : true;

  const canSubmit = soaComplete && otherEvidenceComplete;
  const isComplete =
    request.status === AdditionalEvidenceRequestStatus.Complete;
  return (
    <>
      <DialogTitle data-test="additional-evidence-title">
        Additional evidence summary
      </DialogTitle>
      <Divider component="div" />
      <DialogContent>
        <Typography variant="body2" style={{ marginBottom: 5 }}>
          <div className="ActivityNote">{request.introductionText}</div>
        </Typography>
        {request.statementOfAssuranceRequests
          ? request.statementOfAssuranceRequests
              .sort((a, b) => a.declaration.order - b.declaration.order)
              .map((soa) => (
                <div key={soa.id}>
                  <Grid container spacing={1}>
                    <Grid item xs={12}>
                      <Typography
                        variant="h6"
                        id={`additional-evidence-${soa.declaration.name}`}
                      >
                        {soa.declaration.name} - {soa.declaration.title}
                        {!isComplete && (
                          <Button
                            id={`additional-evidence-${soa.declaration.name}-change-btn`}
                            color="primary"
                            onClick={() => onClick(soa)}
                            className={classes.action}
                          >
                            Change
                          </Button>
                        )}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2">
                        {soa.selectedDeclarationOption
                          ? soa.selectedDeclarationOption.optionText
                          : "Not set"}
                      </Typography>
                    </Grid>
                    {soa.relatedDate ? (
                      <Grid item xs={12}>
                        <DatePicker
                          label="Relevant date"
                          readOnly={true}
                          value={new Date(soa.relatedDate)}
                          dataTest="relevant-date"
                        />
                      </Grid>
                    ) : null}
                    {soa.relatedDescription ? (
                      <Grid item xs={12}>
                        <TextField
                          data-test="relevant-description"
                          multiline
                          rows={5}
                          label="Relevant description"
                          readOnly={true}
                          value={soa.relatedDescription}
                        />
                      </Grid>
                    ) : null}
                  </Grid>
                </div>
              ))
          : null}
        {request.isOtherEvidenceRequired ? (
          <>
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <Typography variant="h6">
                  Other evidence
                  {!isComplete && (
                    <Button
                      id="additional-evidence-other-evidence-change-btn"
                      color="primary"
                      onClick={() => onClick("other")}
                      className={classes.action}
                    >
                      Change
                    </Button>
                  )}
                </Typography>
              </Grid>
              {request.otherEvidenceResponseText ? (
                <Grid item xs={12}>
                  <TextField
                    data-test="other-evidence-response-text"
                    multiline
                    rows={5}
                    label="Other evidence"
                    readOnly={true}
                    value={request.otherEvidenceResponseText}
                  />
                </Grid>
              ) : null}
              {request.allowAttachments && request.attachments ? (
                <Grid item xs={12}>
                  <AttachmentList
                    canEdit={false}
                    attachments={request.attachments}
                    getBlob={onDownload}
                  />
                </Grid>
              ) : null}
            </Grid>
          </>
        ) : null}
      </DialogContent>
      <DialogActions className={classes.dialogActions}>
        <Button
          onClick={onClose}
          color="primary"
          data-test="close-additional-evidence-button"
        >
          Close
        </Button>
        {!isComplete ? (
          <Button
            onClick={onSubmit}
            color="primary"
            data-test="confirm-additional-evidence-button"
            disabled={!canSubmit}
          >
            Submit
          </Button>
        ) : null}
      </DialogActions>
    </>
  );
};
